type DrawWaveformOptions = {
  canvas: HTMLCanvasElement;
  waveformData: number[];
  duration: number;
  currentTime: number;
};

export const generateWaveformData = async (
  audioUrl: string,
  samples = 560
): Promise<number[]> => {
  const AudioContextConstructor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

  const audioContext = new AudioContextConstructor();

  try {
    const response = await fetch(audioUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch audio file: ${response.status}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const rawData = audioBuffer.getChannelData(0);
    const blockSize = Math.max(1, Math.floor(rawData.length / samples));
    const filteredData: number[] = [];

    for (let i = 0; i < samples; i++) {
      const blockStart = i * blockSize;

      if (blockStart >= rawData.length) {
        break;
      }

      const blockEnd = Math.min(blockStart + blockSize, rawData.length);
      let peak = 0;

      for (let j = blockStart; j < blockEnd; j++) {
        peak = Math.max(peak, Math.abs(rawData[j]));
      }

      filteredData.push(peak);
    }

    const maxPeak = Math.max(...filteredData, 1);

    return filteredData.map((value) => value / maxPeak);
  } finally {
    await audioContext.close();
  }
};

export const drawWaveformOnCanvas = ({
  canvas,
  waveformData,
  duration,
  currentTime
}: DrawWaveformOptions) => {
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return;
  }

  const width = canvas.width;
  const height = canvas.height;
  const center = height / 2;

  ctx.clearRect(0, 0, width, height);

  ctx.fillStyle = '#e5e7eb';
  ctx.fillRect(0, 0, width, height);

  if (waveformData.length === 0) {
    return;
  }

  ctx.lineWidth = 1;

  const playedRatio = duration > 0 ? Math.max(0, Math.min(1, currentTime / duration)) : 0;
  const playedX = playedRatio * width;

  const barStep = 2;
  const barCount = Math.max(1, Math.floor(width / barStep));

  ctx.beginPath();

  for (let i = 0; i < barCount; i++) {
    const x = i * barStep + 0.5;

    if (x > playedX) {
      continue;
    }

    const sourceIndex = Math.min(
      waveformData.length - 1,
      Math.floor((i / barCount) * waveformData.length)
    );

    const barHeight = (waveformData[sourceIndex] * height) / 2;

    ctx.moveTo(x, center - barHeight);
    ctx.lineTo(x, center + barHeight);
  }

  ctx.strokeStyle = '#1d4ed8';
  ctx.stroke();

  ctx.beginPath();

  for (let i = 0; i < barCount; i++) {
    const x = i * barStep + 0.5;

    if (x <= playedX) {
      continue;
    }

    const sourceIndex = Math.min(
      waveformData.length - 1,
      Math.floor((i / barCount) * waveformData.length)
    );

    const barHeight = (waveformData[sourceIndex] * height) / 2;

    ctx.moveTo(x, center - barHeight);
    ctx.lineTo(x, center + barHeight);
  }

  ctx.strokeStyle = '#60a5fa';
  ctx.stroke();
};
